import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import Input from "../../../component-lib/Atoms/Input/Input";
import { ORDER_STATUS } from "../constant";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

interface OrderStatusDialogProps {
  isOpen: boolean;
  label: string;
  defaultSelectedData: any;
  handleData: (data: any) => void;
  handleClose: () => void;
}

export default function OrderStatusDialog({
  isOpen = false,
  label = "Your label here",
  defaultSelectedData,
  handleClose,
  handleData,
}: OrderStatusDialogProps) {
  const EDIT_CONTACT = {
    ORDER_STATUS: "orderStatus",
    ORDER_QUANTITY: "orderQuantity",
  };

  const isOrderLocked =
    defaultSelectedData?.data?.orderStatus === "Delivered";
  let filteredOrderStatus: typeof ORDER_STATUS;

  switch (defaultSelectedData?.data?.orderStatus) {
    case "Order Placed":
    case "Re-Order":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) => orderStatus.value !== "Requested",
      );
      break;
    case "In Progress":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) =>
          orderStatus.value !== "Order Placed" &&
          orderStatus.value !== "Re-Order" &&
          orderStatus.value !== "Requested",
      );
      break;
    case "Shipped":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) =>
          orderStatus.value !== "Order Placed" &&
          orderStatus.value !== "In Progress" &&
          orderStatus.value !== "Re-Order" &&
          orderStatus.value !== "Requested",
      );
      break;
    case "Delivered":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) => orderStatus.value === "Delivered",
      );
      break;
    case "Cancelled":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) => orderStatus.value !== "Requested",
      );
      break;
    case "Requested":
      filteredOrderStatus = ORDER_STATUS.filter(
        (orderStatus) => orderStatus.value !== "Re-Order",
      );
      break;
    default:
      filteredOrderStatus = [];
  }

  const currentStatus = defaultSelectedData?.data?.orderStatus;
  // Always show the current status in the dropdown even if it's no longer a
  // normally-selectable option (e.g. "Re-Order" set from the inventory list),
  // so the modal reflects the order's actual status.
  if (
    currentStatus &&
    !filteredOrderStatus.some((s) => s.value === currentStatus)
  ) {
    filteredOrderStatus = [
      { label: currentStatus, value: currentStatus },
      ...filteredOrderStatus,
    ];
  }

  const validationSchema = Yup.object().shape({
    [EDIT_CONTACT?.ORDER_STATUS]: Yup.string()
      .matches(VALIDATION_CONSTANTS.NAME, "Name can only contain characters")
      .required("Order Status is required"),
    [EDIT_CONTACT?.ORDER_QUANTITY]: Yup.string()
      .matches(VALIDATION_CONSTANTS.NUMERIC, "Quantity must be a number")
      .required("Quantity is required"),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [EDIT_CONTACT?.ORDER_STATUS]: `${defaultSelectedData?.data?.orderStatus || ""}`,
          [EDIT_CONTACT?.ORDER_QUANTITY]: `${defaultSelectedData?.data?.orderQuantity ?? ""}`,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {label}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div className="mt-5">
                  <SelectDropdown
                    label="Order Status"
                    value={values[EDIT_CONTACT?.ORDER_STATUS]}
                    options={filteredOrderStatus}
                    onChangeCb={(e) =>
                      setFieldValue(EDIT_CONTACT?.ORDER_STATUS, e.target.value)
                    }
                    onBlur={handleBlur}
                    error={
                      touched[EDIT_CONTACT?.ORDER_STATUS] &&
                      Boolean(errors[EDIT_CONTACT?.ORDER_STATUS])
                    }
                    helperText={
                      touched[EDIT_CONTACT?.ORDER_STATUS]
                        ? errors[EDIT_CONTACT?.ORDER_STATUS]
                        : undefined
                    }
                  />
                </div>
                <div className="mt-5">
                  <Input
                    textFieldLabel="Quantity"
                    type="number"
                    disabled={isOrderLocked}
                    size="small"
                    name={EDIT_CONTACT?.ORDER_QUANTITY}
                    value={values[EDIT_CONTACT?.ORDER_QUANTITY]}
                    onChangeCb={(e) =>
                      setFieldValue(
                        EDIT_CONTACT?.ORDER_QUANTITY,
                        e.target.value,
                      )
                    }
                    onBlur={handleBlur}
                    error={
                      touched[EDIT_CONTACT?.ORDER_QUANTITY] &&
                      Boolean(errors[EDIT_CONTACT?.ORDER_QUANTITY])
                    }
                    helperText={
                      touched[EDIT_CONTACT?.ORDER_QUANTITY]
                        ? errors[EDIT_CONTACT?.ORDER_QUANTITY]
                        : undefined
                    }
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={() => {
                    handleClose();
                  }}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  disabled={isOrderLocked}
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "8px 55px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
